import React, {useEffect, useState} from 'react';
import {injectIntl} from 'react-intl';
import {useHistory} from 'react-router-dom';
import axios from 'axios';
import Button from '@FormComponents/Button';
import SearchField from '@FormComponents/SearchField';
import TextField from '@FormComponents/TextField';
import stringSanitizer from 'stringSanitizer';
import filtersToQueryString from 'filtersToQueryString';
import Icon from '@Components/Icon';
import {pushFindEmployeesEvent} from 'dataLayerHelper';
import {Helmet} from 'rnd-helmet';

const SearchForm = ({intl, searchParams}) => {
  let locationFieldValue = '';
  if (searchParams && searchParams.state && !searchParams.city) {
    locationFieldValue = searchParams.state;
  } else if (searchParams && searchParams.state && searchParams.city) {
    const city = searchParams.city;
    const state = searchParams.state;
    locationFieldValue = `${city}, ${state}`;
  } else if (searchParams && !searchParams.state && searchParams.city) {
    locationFieldValue = searchParams.city;
  } else if (searchParams && searchParams.zip) {
    locationFieldValue = searchParams.zip;
  }

  let searchFieldValue = '';
  if (searchParams && Object.keys(searchParams).length && searchParams.keyword) {
    searchFieldValue = searchParams.keyword;
  }

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    setState({
      searchField: searchFieldValue,
      enterPressed: 0,
    });
    setLocationValue({locationSearch: locationFieldValue});
  }, [searchParams]);

  const [state, setState] = useState({
    searchField: searchFieldValue,
    enterPressed: 0,
  });
  // eslint-disable-next-line
  const [validationErrors, setValidationErrors] = useState({});
  const [locationValue, setLocationValue] = useState({locationSearch: locationFieldValue});
  const [options, setOptions] = useState([]);
  const [mounted, setMounted] = useState(false);
  const history = useHistory();

  const onChange = (event) => {
    event.persist();
    const name = event.target.name;
    const value = event.target.value;
    setState((prev) => {
      return {
        ...prev,
        [name]: value,
      };
    });
  };

  const getLocationFilterValues = (locationValue) => {
    const searchFilters = {};
    if (locationValue) {
      const postcodeRegex = new RegExp(process.env.REACT_APP_POSTCODE_REGEX);
      if (postcodeRegex.test(locationValue)) {
        // Populate url for zip and city with default mi-25.
        searchFilters.radius = process.env.REACT_APP_DEFAULT_DISTANCE_VALUE;
        searchFilters.zip = locationValue;
        searchFilters.state = null;
        searchFilters.city = null;
      } else {
        searchFilters.zip = null;
        const locationData = locationValue.split(', ');
        if (locationData.length > 1) {
          // Populate url for zip and city with default mi-25.
          searchFilters.radius = process.env.REACT_APP_DEFAULT_DISTANCE_VALUE;
          const zipCityRegex = new RegExp(process.env.REACT_APP_ZIPCITY_REGEX);
          const city = stringSanitizer(locationData[0]);
          if (zipCityRegex.test(city)) {
            const cityParts = city.split('-');
            searchFilters.zip = cityParts[0];
            searchFilters.state = null;
            searchFilters.city = null;
          } else {
            searchFilters.city = locationData[0];
            searchFilters.state = locationData[1];
          }
        } else if (locationData.length === 1) {
          searchFilters.state = locationData[0];
        }
      }
    }

    return searchFilters;
  };

  const submitSearch = (event) => {
    event.preventDefault();
    const errors = {};
    if (!locationValue.locationSearch) {
      errors.location = true;
    }
    if (!state.searchField) {
      errors.keyword = true;
    }
    if (Object.keys(errors).length) {
      setValidationErrors(errors);
    } else {
      const searchFilters = {
        keyword: state.searchField.replace(/[^a-zA-Z\d:]+/g, ' ')
            .replace(new RegExp(process.env.REACT_APP_REMOVE_SYMBOLS_TEXT_SEARCH, 'g'), ''),
        ...getLocationFilterValues(locationValue.locationSearch),
      };
      if (history.location.pathname === '/find-employees/') {
        pushFindEmployeesEvent(
            'find employees – main',
            {
              radius: searchFilters.radius,
              location: locationValue.locationSearch,
              keyword: searchFilters.keyword,
            },
        );
      }

      history.push({
        'pathname': '/find-employees/search-result/',
        'search': filtersToQueryString(searchFilters),
        'hash': history.location.pathname === '/find-employees/' ? 'fromFindEmployee' : '',
      });
      setValidationErrors({});
    }
  };

  const onEnterSubmit = (event) => {
    // Submit search on enter key press.
    if (event.keyCode === 13) {
      submitSearch(event);
    }
  };

  const onClearSearchField = (event) => {
    setState({searchField: ''});
  };

  const getAutocompleteOptions = (e) => {
    if (e.target.value && e.target.value.length) {
      axios.post(`${process.env.REACT_APP_API_PREFIX}/get-callback`, {
        callback: '@Callbacks/getLocationAutocomplete',
        currentRoute: {},
        data: {
          searchText: e.target.value,
        },
      }).then((response) => {
        setOptions(response.data);
        // Dispatch input event once again, as the event listeners in orbit
        // were already triggered, but the data attributes were not yet ready,
        // because setOptions is a promise and is called after the input event.
        if (typeof document !== 'undefined') {
          // Dispatch the event only if the event is trusted.
          // isTrusted is true only when the event was triggered by the user.
          // It is false when the event was triggered from a script.
          if (e.isTrusted) {
            const event = new CustomEvent('input', {bubbles: true});
            e.target.dispatchEvent(event);
            document.dispatchEvent(event);
          }
        }
      });
    }
  };

  const locationAutocomplete = (event) => {
    event.persist();
    setLocationValue({[event.target.name]: event.target.value});
    getAutocompleteOptions(event);
    setState((prev) => {
      return {
        ...prev,
      };
    });
  };

  const onLocationEnterSubmit = (event) => {
    // Submit search on enter key press.
    if (event.keyCode === 13) {
      if (state.enterPressed === 0) {
        setState((prev) => {
          return {
            ...prev,
            enterPressed: prev.enterPressed + 1,
          };
        });
      } else {
        submitSearch(event);
        setState((prev) => {
          return {
            ...prev,
            enterPressed: 0,
          };
        });
      }
    }
  };

  const onClearLocationField = (event) => {
    setLocationValue({locationSearch: ''});
    setState((prev) => {
      return {
        ...prev,
      };
    });
  };

  return (
    <>
      {mounted &&
        <Helmet>
          <script type='text/javascript' src='/employee-app/assets/js/auto-suggest.js'/>
        </Helmet>
      }
      <div
        className={`form-group search-form__input form-group--jobs-search ${validationErrors.keyword ? 'form-group--error' : ''}`}
      >
        <div className="form-group__input form-group__search form-group__input--button clearable-input" data-rs-clearable={true}>
          <SearchField
            id="searchField"
            name="searchField"
            autoComplete="off"
            data-rs-input-highlight={true}
            placeholder={validationErrors.location ?
              intl.formatMessage({id: 'SearchForm.SearchFieldError'}) :
              intl.formatMessage({id: 'SearchForm.SearchFieldPlaceholder'})}
            value={state.searchField}
            onChange={onChange}
            onKeyDown={onEnterSubmit}/>
          <Button
            className="button--icon-only" data-rs-clearable-button={true}
            aria-hidden={false} tabIndex="-1" onClick={onClearSearchField}
          >
            <span>{ intl.formatMessage({id: 'SearchForm.SearchFieldClearText'}) }</span>
            <span className="icon fill-brand--blue" onClick={onClearSearchField}>
              <svg>
                <use xlinkHref="/themes/custom/bluex/dist/assets/image/icons.svg#close" />
              </svg>
            </span>
          </Button>
        </div>
      </div>
      <div className="form-group flex">
        <div className={`form-group mr-xxs flex flex-1 search-form__input ${validationErrors.location ? 'form-group--error' : ''}`}>
          <div
            className="form-group__input form-group__location form-group__input--button flex-1 clearable-input"
            data-rs-clearable={true} data-rs-auto-suggest={true}
          >
            <TextField
              id="geoSearch"
              name="locationSearch"
              autoComplete="off"
              data-rs-input-highlight={true}
              data-rs-auto-suggest-input={true}
              data-rs-auto-suggest-no-result-message={intl.formatMessage({id: 'Autosuggest.Noresults.Message'})}
              placeholder={validationErrors.location ?
                intl.formatMessage({id: 'SearchForm.SearchLocationError'}) :
                intl.formatMessage({id: 'SearchForm.SearchLocationPlaceholder'})}
              value={locationValue.locationSearch}
              onChange={locationAutocomplete}
              onKeyDown={onLocationEnterSubmit}/>
            <button
              className="button--icon-only" data-rs-clearable-button={true}
              aria-hidden={false} tabIndex="-1" onClick={onClearLocationField}
            >
              <span>{ intl.formatMessage({id: 'SearchForm.SearchLocationClearText'}) }</span>
              <span className="icon fill-brand--blue" onClick={onClearLocationField}>
                <svg>
                  <use xlinkHref="/themes/custom/bluex/dist/assets/image/icons.svg#close" />
                </svg>
              </span>
            </button>
            <ul
              className="select-menu__list"
              id="geo-search-as"
              data-rs-auto-suggest-list=""
              data-rs-auto-suggest-values={options.length ? options.splice(0, 10).join(';') : ''}
              data-rs-auto-suggest-separator=";"/>
          </div>
        </div>
        <Button className="button button--icon-mobile bluex-button--preloader button--filled button--m" onClick={submitSearch}>
          <span className="button__text hidden--until-l">{ intl.formatMessage({id: 'SearchForm.SearchButtonText'}) }</span>
          <span className="icon hidden--from-l" aria-hidden="true">
            <Icon type="search"/>
          </span>
        </Button>
      </div>
    </>
  );
};

export default injectIntl(SearchForm);
